import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SwalService } from '../../../../common/services/swal.service';
import { UserRegistrationsService } from '../../../user-registrations/services/user-registrations.service';
import { BanksService } from '../../../banks/services/banks.service';
import {
  BankSummaryDto,
  BranchSummaryDto,
} from '../../../../core/data/new-dtos/bank.dtos';
import { lastValueFrom, Subscription, timeout } from 'rxjs';
import { SharedModule } from '../../../../common/shared.module';
import {
  UserRegistrationCreateDto,
  UserRegistrationResponseDto,
  UserRegistrationRole,
  toRegistrationStatus,
} from '../../../../core/data/new-dtos/user-registration.dtos';
import { MsalService } from '@azure/msal-angular';
import { NgxMaskDirective } from 'ngx-mask';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../../../environments/environment';
import { BankDirectorsService } from '../../../directors/services/bank-directors.service';
import { BankDirectorDto } from '../../../../core/data/new-dtos/bank-director.dtos';

type RegisterView = 'loading' | 'form' | 'rejected' | 'pending' | 'deactivated' | 'invited';

const ROLE_BY_QUERY_PARAM: Record<string, UserRegistrationRole> = {
  supervisor: UserRegistrationRole.Supervisor,
  manager: UserRegistrationRole.Manager,
  retailer: UserRegistrationRole.Retailer,
};

@Component({
  selector: 'auth-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, NgxMaskDirective],
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  providers: [SwalService],
})
export class RegisterComponent implements OnInit, OnDestroy {
  form: FormGroup;
  submitting = false;
  viewState: RegisterView = 'loading';
  registration: UserRegistrationResponseDto | null = null;
  checking = false;
  /** A pre-authorized global/regional invite; its own flow replaces the request form. */
  invite: BankDirectorDto | null = null;
  inviteForm: FormGroup;
  private formReady = false;
  roleLocked = false;
  /** Which identity fields the B2C account supplied, and are therefore read-only. */
  lockedFromB2C = { firstName: false, lastName: false, email: false };

  get anyLockedFromB2C(): boolean {
    return this.lockedFromB2C.firstName || this.lockedFromB2C.lastName || this.lockedFromB2C.email;
  }

  readonly Role = UserRegistrationRole;

  roles = [
    { label: 'Supervisor', value: UserRegistrationRole.Supervisor },
    { label: 'Gerente', value: UserRegistrationRole.Manager },
    { label: 'Retailer', value: UserRegistrationRole.Retailer },
  ];

  banks: BankSummaryDto[] = [];
  branches: BranchSummaryDto[] = [];
  // Keyed by the row itself so removing a row can never desynchronise the lists
  private branchesByRow = new Map<AbstractControl, BranchSummaryDto[]>();
  private subsByRow = new Map<AbstractControl, Subscription>();
  private subs: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private userRegs: UserRegistrationsService,
    private swal: SwalService,
    private route: ActivatedRoute,
    private banksService: BanksService,
    private msalService: MsalService,
    private auth: AuthService,
    private directorsService: BankDirectorsService,
    private router: Router,
  ) {
    this.inviteForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
    });

    this.form = this.fb.group({
      // B2C Azure ID - populated from the authenticated session
      targetB2CId: new FormControl<string | null>(null),

      // Required basic info
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      documentNumber: ['', [Validators.required, Validators.pattern(/^\d{3}-\d{7}-\d$/)]],
      requestedRole: new FormControl<UserRegistrationRole | null>(null, Validators.required),

      // Optional
      bankId: new FormControl<number | null>(null),
      branchId: new FormControl<number | null>(null),
      notes: [''],

      assignments: this.fb.array([]),
    });
  }

  get isSupervisor(): boolean {
    return this.form.get('requestedRole')!.value === UserRegistrationRole.Supervisor;
  }

  get assignments(): FormArray {
    return this.form.get('assignments') as FormArray;
  }

  branchesFor(row: AbstractControl): BranchSummaryDto[] {
    return this.branchesByRow.get(row) ?? [];
  }

  async ngOnInit(): Promise<void> {
    // The identity we prefill from resolves asynchronously; without this the fields
    // are populated from an empty profile and stay blank and editable.
    await this.auth.profileReady();

    // An active user has no business on this screen
    if (this.auth.bankProfile) {
      await this.router.navigate(['/employees']);
      return;
    }

    // Invited directors are pre-authorized: no request, no approval, no role to
    // pick. They only confirm their personal information.
    this.invite = await this.loadInvite();
    if (this.invite) {
      this.inviteForm.patchValue({
        firstName: this.invite.name,
        lastName: this.invite.lastName,
      });
      this.viewState = 'invited';
      return;
    }

    this.viewState = await this.resolveViewState();

    if (this.viewState === 'form' || this.viewState === 'rejected') {
      await this.setUpForm();
    }
  }

  /** Null when the caller has no invite (or the check fails - the normal flow continues). */
  private async loadInvite(): Promise<BankDirectorDto | null> {
    try {
      return await lastValueFrom(this.directorsService.getMyInvite().pipe(timeout(15000)));
    } catch {
      return null;
    }
  }

  get inviteRoleLabel(): string {
    return this.invite?.role === 'Global' ? 'usuario global' : 'usuario regional';
  }

  async submitInvite(): Promise<void> {
    if (this.inviteForm.invalid) {
      this.inviteForm.markAllAsTouched();
      return;
    }

    if (this.submitting) return;
    this.submitting = true;
    this.swal.showLoading('Completando tu registro...');

    try {
      const value = this.inviteForm.value;
      await lastValueFrom(
        this.directorsService.acceptInvite({
          name: value.firstName,
          lastName: value.lastName,
        }),
      );

      // The account is now linked; the fresh profile carries the new role
      await this.auth.refreshProfile();
      this.swal.dismiss();
      await this.router.navigate(['/employees']);
    } catch (e: any) {
      this.swal.error(e?.error?.error ?? 'No se pudo completar el registro. Inténtalo de nuevo.');
    } finally {
      this.submitting = false;
    }
  }

  /**
   * Asks the API what the caller's latest request looks like. 'form' is the safe
   * default: an unreachable status endpoint must never block a genuine applicant,
   * and a duplicate submission is refused by the server anyway.
   */
  private async resolveViewState(): Promise<RegisterView> {
    try {
      // Bounded: this screen's whole job is to show a state, so it must never be
      // left spinning if the request stalls.
      this.registration = await lastValueFrom(
        this.userRegs.getMine().pipe(timeout(15000)),
      );
    } catch {
      this.registration = null;
    }

    switch (toRegistrationStatus(this.registration?.status)) {
      case 'Pending':
        return 'pending';
      case 'Rejected':
        return 'rejected';
      // Approved with no bank profile means the account was deactivated afterwards
      case 'Approved':
        return 'deactivated';
      default:
        return 'form';
    }
  }

  private async setUpForm(): Promise<void> {
    if (this.formReady) return;
    this.formReady = true;

    this.prefillFromB2C();
    this.applyRequestedRoleFromQuery();

    try {
      const banks = await lastValueFrom(this.banksService.getBanks());
      this.banks = banks ?? [];
    } catch {
      this.banks = [];
    }

    const sRole = this.form
      .get('requestedRole')!
      .valueChanges.subscribe((role: UserRegistrationRole | null) => {
        this.applyRoleValidators(role);

        if (role !== UserRegistrationRole.Supervisor) {
          while (this.assignments.length) {
            this.removeAssignment(0);
          }
        }
      });
    this.subs.push(sRole);

    this.applyRoleValidators(this.form.get('requestedRole')!.value);

    const sBank = this.form
      .get('bankId')!
      .valueChanges.subscribe(async (bankId) => {
        this.form.get('branchId')!.setValue(null);
        this.branches = bankId ? await this.loadBranches(bankId) : [];
      });
    this.subs.push(sBank);
  }

  private applyRequestedRoleFromQuery(): void {
    const raw = (
      this.route.snapshot.queryParamMap.get('role') ??
      sessionStorage.getItem('pendingRegistrationRole') ??
      ''
    ).toLowerCase();
    sessionStorage.removeItem('pendingRegistrationRole');

    // A role already established at sign-up wins over the link the user arrived through
    if (this.roleLocked) return;

    const preset = ROLE_BY_QUERY_PARAM[raw];
    if (preset === undefined) return;

    const control = this.form.get('requestedRole')!;
    control.setValue(preset);
    control.disable({ emitEvent: false });
    this.roleLocked = true;
    this.applyRoleValidators(preset);
  }

  get lockedRoleLabel(): string {
    const value = this.form.get('requestedRole')!.value;
    return this.roles.find((r) => r.value === value)?.label ?? '';
  }

  /**
   * Identity comes from the account created during B2C sign-up. Anything it gives us is
   * shown read-only, so an applicant cannot register under a different name or address
   * than the one they authenticated with. A field it does not supply stays editable
   * rather than leaving the form impossible to submit.
   */
  private prefillFromB2C(): void {
    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length === 0) {
      this.prefillFromImpersonatedUser();
      return;
    }

    const account = accounts[0];
    const claims = (account.idTokenClaims ?? {}) as any;

    const firstName = claims.given_name || account.name?.split(' ')[0] || '';
    const lastName =
      claims.family_name || account.name?.split(' ').slice(1).join(' ') || '';
    const email = account.username || claims.email || claims.emails?.[0] || '';

    // localAccountId is the Azure B2C object id
    this.form.patchValue({
      targetB2CId: account.localAccountId,
      firstName,
      lastName,
      email,
    });

    this.lockedFromB2C = {
      firstName: !!firstName,
      lastName: !!lastName,
      email: !!email,
    };

    // B2C may already carry the role granted at sign-up. The user flows emit it
    // as extension_EmployeeRole ("Manager"); extension_Role is the legacy name.
    const claimRole = this.mapRole(claims.extension_EmployeeRole ?? claims.extension_Role);
    if (claimRole !== undefined) {
      const control = this.form.get('requestedRole')!;
      control.setValue(claimRole);
      control.disable({ emitEvent: false });
      this.roleLocked = true;
      this.applyRoleValidators(claimRole);
    }
  }

  /** Local development has no B2C account; fall back to the impersonated user. */
  private prefillFromImpersonatedUser(): void {
    if (!environment.devAuth?.enabled) return;

    const profile = this.auth.bankProfile;
    const azureId = this.auth.azureId ?? '';

    this.form.patchValue({
      targetB2CId: azureId,
      firstName: profile?.name ?? '',
      lastName: profile?.lastName ?? '',
      email: `${azureId}@dev.local`,
    });

    this.lockedFromB2C = {
      firstName: !!profile?.name,
      lastName: !!profile?.lastName,
      email: !!azureId,
    };

    // Mirror the real flow, where the role comes from the account rather than the form
    const role = this.mapRole(profile?.role);
    if (role !== undefined) {
      const control = this.form.get('requestedRole')!;
      control.setValue(role);
      control.disable({ emitEvent: false });
      this.roleLocked = true;
      this.applyRoleValidators(role);
    }
  }

  /** Accepts the numeric enum, its string name, or the query-param spelling. */
  private mapRole(value: unknown): UserRegistrationRole | undefined {
    if (value === null || value === undefined || value === '') return undefined;

    if (typeof value === 'number' && UserRegistrationRole[value] !== undefined) {
      return value as UserRegistrationRole;
    }

    const key = String(value).toLowerCase();
    return ROLE_BY_QUERY_PARAM[key];
  }

  addAssignment() {
    const group = this.fb.group({
      bankId: [null, [Validators.required]],
      branchId: [null],
    });
    this.assignments.push(group);

    const sub = group.get('bankId')!.valueChanges.subscribe(async (bankId) => {
      group.get('branchId')!.setValue(null);
      this.branchesByRow.set(group, bankId ? await this.loadBranches(bankId) : []);
    });
    this.subsByRow.set(group, sub);
  }

  removeAssignment(index: number) {
    const row = this.assignments.at(index);
    this.subsByRow.get(row)?.unsubscribe();
    this.subsByRow.delete(row);
    this.branchesByRow.delete(row);
    this.assignments.removeAt(index);
  }

  private async loadBranches(bankId: number): Promise<BranchSummaryDto[]> {
    try {
      return (await lastValueFrom(this.banksService.getBranches(bankId))) ?? [];
    } catch {
      return [];
    }
  }

  async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const role = value.requestedRole as UserRegistrationRole;

    const body: UserRegistrationCreateDto = {
      targetB2CId: value.targetB2CId,
      firstName: value.firstName,
      lastName: value.lastName,
      email: value.email,
      documentNumber: value.documentNumber,
      notes: value.notes || null,
      requestedRole: role,
      ...(role === UserRegistrationRole.Supervisor
        ? { bankId: null, branchId: null, assignments: value.assignments }
        : { bankId: value.bankId, branchId: value.branchId }),
    };

    this.submitting = true;
    this.swal.showLoading('Enviando solicitud de registro...');

    try {
      // The API returns the created request itself, not the {data} envelope
      this.registration = (await lastValueFrom(
        this.userRegs.createRegistration(body),
      )) as unknown as UserRegistrationResponseDto;

      this.viewState = 'pending';
      await this.swal.success(
        'Su solicitud fue enviada y será revisada por un administrador.',
      );
    } catch (e: any) {
      const message: string = e?.error?.error ?? '';
      const normalized = message
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();

      // The server refuses duplicates; reflect the state it is telling us about
      if (e?.status === 409 && normalized.includes('pendiente')) {
        this.viewState = 'pending';
        await this.recheck();
      } else if (e?.status === 409 && normalized.includes('ya esta registrada')) {
        this.viewState = 'deactivated';
      } else {
        this.swal.error(message || 'No se pudo enviar la solicitud de registro.');
      }
    } finally {
      this.submitting = false;
      this.swal.dismiss();
    }
  }

  /** Re-asks the server whether anything changed while the applicant waited. */
  async recheck(): Promise<void> {
    if (this.checking) return;
    this.checking = true;

    try {
      // Approved and activated in the meantime: walk straight in, no re-login
      if (await this.auth.refreshProfile()) {
        await this.router.navigate(['/employees']);
        return;
      }

      const next = await this.resolveViewState();

      if (next === this.viewState) {
        await this.swal.fire({
          icon: 'info',
          html:
            this.viewState === 'deactivated'
              ? 'Tu cuenta sigue desactivada. Contacta a tu supervisor.'
              : 'Tu solicitud sigue pendiente de aprobación.',
        });
        return;
      }

      this.viewState = next;
      if (next === 'form' || next === 'rejected') {
        await this.setUpForm();
      }
    } finally {
      this.checking = false;
    }
  }

  /** The sign-up session is unusable until the request is approved, so end it. */
  signOut() {
    void this.auth.logOut();
  }

  cancel() {
    this.signOut();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.subsByRow.forEach((s) => s.unsubscribe());
    this.swal.dismiss();
  }

  private applyRoleValidators(role: UserRegistrationRole | null) {
    const bankCtrl = this.form.get('bankId')!;
    const branchCtrl = this.form.get('branchId')!;

    if (role === UserRegistrationRole.Manager || role === UserRegistrationRole.Retailer) {
      bankCtrl.setValidators([Validators.required]);
      branchCtrl.setValidators([Validators.required]);
      bankCtrl.updateValueAndValidity({ emitEvent: false });
      branchCtrl.updateValueAndValidity({ emitEvent: false });
      return;
    }

    // Supervisors use the assignments list; with no role chosen yet, require nothing
    bankCtrl.clearValidators();
    branchCtrl.clearValidators();
    bankCtrl.setValue(null, { emitEvent: false });
    branchCtrl.setValue(null, { emitEvent: false });
    bankCtrl.updateValueAndValidity({ emitEvent: false });
    branchCtrl.updateValueAndValidity({ emitEvent: false });
  }
}
